import { measureText } from '@remotion/layout-utils';
import { COMPOSITION_DIMENSIONS, VIDEO_QUALITY } from '@/App/remotion/constants';
import { TextElementTextStyle } from '@/App/remotion/MainPlayer/types/Element/TextElement';

export const calculateActualLineCount = (
  text: string,
  containerWidthPx: number,
  fontFamily: string,
  fontSize: number,
  textElementStyle?: TextElementTextStyle
): number => {
  if (!text || !text.trim()) {
    return 0;
  }

  try {
    const words = text.trim().split(/\s+/);
    let lineCount = 1;
    let currentLineWidth = 0;

    const effectiveContainerWidth = Math.max(0, containerWidthPx - 32);

    const fontWeight = textElementStyle?.fontWeight || 'normal';

    const spaceWidth = measureText({
      text: ' ',
      fontFamily,
      fontSize,
      fontWeight,
      textTransform: textElementStyle?.textTransform
    }).width;

    for (let i = 0; i < words.length; i++) {
      const word = words[i];

      const baseWordWidth = measureText({
        text: word,
        fontFamily,
        fontSize,
        fontWeight,
        textTransform: textElementStyle?.textTransform
      }).width;

      const wordWithSpaceWidth = i < words.length - 1 ? baseWordWidth + spaceWidth : baseWordWidth;

      if (currentLineWidth + wordWithSpaceWidth > effectiveContainerWidth && currentLineWidth > 0) {
        lineCount++;
        currentLineWidth = wordWithSpaceWidth;
      } else {
        currentLineWidth += wordWithSpaceWidth;
      }
    }

    return Math.max(1, lineCount);
  } catch (error) {
    const wordCount = text.trim().split(/\s+/).length;
    return Math.ceil(wordCount / 4); // rough estimate of 4 words per line
  }
};

export const calculateContainerWidth = (input: string): number | undefined => {
  const containerWidthPercent = input === 'LANDSCAPE' ? 60 : 75;

  const canvasWidth = COMPOSITION_DIMENSIONS[input][VIDEO_QUALITY.FULL_HD].WIDTH;

  return (containerWidthPercent / 100) * canvasWidth;
};

export const calculateBackgroundHeightBasedOnLineCount = (lineCount: number): number => {
  if (lineCount <= 1) {
    return 49;
  }
  if (lineCount === 2) {
    return 29;
  }
  if (lineCount === 3) {
    return 19;
  }
  if (lineCount >= 4) {
    return 15;
  }

  // More granular calculation for edge cases
  const baseHeight = 49;
  const heightReductionPerLine = 15;
  return Math.max(10, baseHeight - (lineCount - 1) * heightReductionPerLine);
};

export const calculateDynamicBackground = (
  text: string,
  containerWidthPx: number,
  dynamicStyle?: TextElementTextStyle
): string => {
  const fontFamily = dynamicStyle?.fontFamily || 'Inter';
  const fontSize = dynamicStyle?.fontSize || 84;
  const currentBackground = dynamicStyle?.background;

  // Extract and preserve existing height if available
  let newHeight: number;
  // Match the second occurrence of transparent with percentage (the actual height value)
  // Pattern: transparent 0%, transparent HEIGHT%, color HEIGHT%
  const heightMatch = currentBackground?.match(/transparent\s+0%,\s*transparent\s+(\d+(?:\.\d+)?)%/);

  if (heightMatch) {
    // Preserve existing height (user may have manually set it)
    newHeight = parseFloat(heightMatch[1]);
  } else {
    // No existing height found, calculate from line count
    const lineCount = calculateActualLineCount(text, containerWidthPx, fontFamily, fontSize, dynamicStyle);
    newHeight = calculateBackgroundHeightBasedOnLineCount(lineCount);
  }

  const colorMatch = currentBackground?.match(/rgba?\([^)]+\)/);
  const bgColor = colorMatch ? colorMatch[0] : 'rgba(255, 255, 255, 1)';

  return `linear-gradient(180deg, transparent 0%, transparent ${newHeight}%, ${bgColor} ${newHeight}%, ${bgColor} 100%)`;
};
